<template>
<div>
    <div class="disability-inline-layout">
            <div class="disability-inline-checkbox-wrap">
                <fe-stepper-checkbox-panel
                    v-model="panelValues.disabilities"
                    :items="disabilityItems"
                    width="100%"
                    itemValue="id"
                    itemText="name"
                    @input="restrictSelection"
                />
            </div>
            <div class="disability-inline-options">
                <fe-remote-combo
                    class="school-year-field-cell"
                    label="School Year"
                    infoText="If a student was exited at any time during the year, they will be considered exited for the entire year."
                    v-model="panelValues.school_year"
                    rootProperty="years"
                    url="qry.php?action=get&property=school_years"
                    :error="schoolYearRequiredError"
                    :error-messages="schoolYearRequiredError ? 'School Year is required.' : ''"
                    data-test="disability-school-year-filter-field"
                />
                <div class="disability-inline-toggles">
                    <v-switch v-model="panelValues.restrict" label="Restrict Results" hide-details :ripple="false" data-test="disability-restrict-results-switch" />
                    <v-switch v-model="panelValues.require_all" label="Require All" hide-details :ripple="false" data-test="disability-require-all-switch" />
                </div>
            </div>
        </div>
</div>
</template>

<script>
import * as Util from '../../Util'
import FilterUtil from '../../FilterPanelUtil'

export default {
    name: 'DisabilityPanel',
    rank: 1,

    props: {
        existingFilter: { type: Object, default: null },
    },

    data() {
        return {
            displayName: 'Disability',
            type: 'disability',
            disabilityItems: [],
            panelValues: {
                disabilities: [],
                require_all: false,
                school_year: null,
                restrict: true
            },
            select_all_item: { id: -1, code: 'ALLDIS', name: 'Select All' },
        }
    },

    computed: {
        schoolYearRequiredError() {
            const selected = (this.panelValues.disabilities || []).filter(x => x.id !== -1)
            return selected.length > 0 && !this.panelValues.school_year
        },
        inlineValid() {
            const selected = (this.panelValues.disabilities || []).filter(x => x.id !== -1)
            return selected.length > 0 && !!this.panelValues.school_year
        },
        isDirty() {
            // Monitor form state to enable/disable apply button appropriately
            const applied = this.existingFilter?.editorParams
            if (!applied) return false
            const current = this.panelValues

            if (applied.restrict !== current.restrict || applied.require_all !== current.require_all) return true

            const appliedSchoolYearId = applied.school_year?.id ?? null
            const currentSchoolYearId = current.school_year?.id ?? null
            if (appliedSchoolYearId !== currentSchoolYearId) return true

            const appliedIds = !Array.isArray(applied.disabilities) ? [] : applied.disabilities.map(x => x?.id).filter(id => id != null && id !== -1).sort((a, b) => a - b)
            const currentIds = !Array.isArray(current.disabilities) ? [] : current.disabilities.filter(x => x?.id !== -1).map(x => x?.id).sort((a, b) => a - b)
            return appliedIds.length !== currentIds.length || appliedIds.some((id, i) => id !== currentIds[i])
        },
    },

    watch: {
        existingFilter: {
            handler(f) {
                // Sync form state from the applied filter when it changes; clear form when the filter is removed.
                if (f?.editorParams) {
                    const ep = f.editorParams
                    this.panelValues.restrict = ep.restrict
                    this.panelValues.disabilities = ep.disabilities ? [...ep.disabilities] : []
                    this.panelValues.require_all = ep.require_all
                    this.panelValues.school_year = ep.school_year ? { ...ep.school_year } : null
                } else {
                    this.panelValues.disabilities = []
                    this.panelValues.require_all = false
                    this.panelValues.school_year = null
                    this.panelValues.restrict = true
                    this.$nextTick(() => this.loadDefaultSchoolYear())
                }
            },
            immediate: true,
        },
        inlineValid: {
            handler(v) {
                this.$emit('valid-change', v)
            },
            immediate: true,
        },
        schoolYearRequiredError: {
            handler(v) {
                this.$emit('invalid-change', v)
            },
            immediate: true,
        },
        isDirty: {
            handler(v) {
                this.$emit('dirty-change', v)
            },
            immediate: true,
        },
    },

    mounted() {
        this.loadDefaultSchoolYear()
        this.loadDisabilities()
        this.$emit('valid-change', this.inlineValid)
    },

    methods: {
        loadDefaultSchoolYear() {
            this.$axios.get('qry.php?action=get&property=school_years').then((r) => {
                const years = r.data?.years || []
                if (years.length && !this.panelValues.school_year) {
                    this.$set(this.panelValues, 'school_year', years[0])
                }
            })
        },
        getFilter() {
            if (this.$_.isEmpty(this.panelValues.disabilities)) return null
            const disabilities = this.panelValues.disabilities.filter(x => x.id !== -1)
            if (!disabilities.length) return null
            const name = disabilities.map(x => x.display_name || x.description).join(', ')
            const ids = FilterUtil.collectIdsFromSelection(this.panelValues.disabilities, { idKey: 'id', parseInteger: true })
            const filter = {
                type: this.type,
                id: Util.UUIDv4(),
                restrict: this.panelValues.restrict,
                report: true,
                params: this.$_.pickBy({
                    student_disability_type_id: ids,
                    require_all: this.panelValues.require_all,
                    school_year_id: this.panelValues.school_year?.id,
                }, x => !this.$_.isBlank(x)),
            }
            FilterUtil.attachFilterDescriptor(filter, {
                editor: this,
                editorParams: {
                    restrict: this.panelValues.restrict,
                    disabilities: disabilities ? [...disabilities] : null,
                    require_all: this.panelValues.require_all,
                    school_year: this.panelValues.school_year ? { ...this.panelValues.school_year } : null,
                },
                chipText: name,
                tooltipData: this.$_.pickBy({
                    'Disability': name,
                    'Restrict': this.panelValues.restrict,
                    'Require All': this.panelValues.require_all,
                    'School Year': this.panelValues.school_year?.name,
                }, x => !this.$_.isBlank(x)),
                grouper: {
                    id: filter.id,
                    name,
                    type: 'propertyArray',
                    args: { path: `filters.${filter.id}`, property: 'value' },
                },
            })
            return filter
        },

        loadDisabilities() {
            this.$modelGet('studentDisabilityType')
                .then((res) => {
                    // $modelGet resolves with the rootProperty value (the array), not response.data
                    const data = Array.isArray(res) ? res : (res?.data?.student_disability_type ?? res?.student_disability_type ?? [])
                    const filtered = []
                    data.forEach((itm) => {
                        if (!itm.display_name_group) {
                            itm.name = itm.display_name || itm.description
                            filtered.push(itm)
                        } else {
                            const o = data.find(x => x.id == itm.display_name_group[0]) // id can be string
                            if (!filtered.find(x => x.display_name === o.display_name)) {
                                filtered.push({ ...o, name: o.display_name || o.description })
                            }
                        }
                    })
                    this.disabilityItems = [this.select_all_item, ...filtered]
                })
        },

        restrictSelection(allSelections, newSelection) {
            if (newSelection.id === -1) {
                if (this.panelValues.disabilities.length === this.disabilityItems.length - 1) {
                    this.panelValues.disabilities = []
                } else this.panelValues.disabilities = [...this.disabilityItems]
            } else if (allSelections.length !== this.disabilityItems.length) {
                this.panelValues.disabilities = this.panelValues.disabilities.filter(x => x.id !== -1)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.disability-inline-layout {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
}
.disability-inline-checkbox-wrap {
    min-width: 0;
}
.disability-inline-options {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 62px;
    min-width: 0;
}
.disability-inline-options .school-year-field-cell {
    flex: 0 1 auto;
    width: 268px;
}
.disability-inline-options .disability-inline-toggles {
    flex: 0 0 auto;
    min-width: 0;
}
.disability-inline-options > *:not(.disability-inline-toggles):not(.school-year-field-cell) {
    flex: 1;
    min-width: 0;
}
.disability-inline-toggles {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
}
.disability-inline-checkbox-wrap ::v-deep .v-list.fe-stepper-checkbox {
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    grid-auto-flow: row;
    gap: 0 16px;
}
.disability-inline-checkbox-wrap ::v-deep .v-list.fe-stepper-checkbox > .v-list-item {
    min-width: 0;
    max-width: 100%;
}
</style>
